/**
 * Standalone dialog to review company CSV identity conflicts for a finished job.
 * Works after the import wizard is closed — opened from the progress pill or Import History.
 *
 * Safe outside WorkspaceProvider: loads the job by id (RLS-scoped) and uses the
 * job's workspace_id for company lookups and force-create.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useOptionalWorkspace } from '@/contexts/WorkspaceContext';
import { buildConflictsFromImportJob } from '@/lib/csvImportConflicts';
import type { ImportConflictRow } from '@/hooks/useCSVImport';
import { CSVCompanyConflictReview } from './CSVCompanyConflictReview';
import { log } from '@/lib/logger';

interface CSVImportConflictReviewDialogProps {
  jobId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional override when context workspace is unavailable (e.g. app-shell pill). */
  workspaceId?: string | null;
  /** Called when every identity conflict is resolved (force or skip). */
  onAllResolved?: () => void;
}

type CompanyMatch = {
  id: string;
  name: string;
  domain: string | null;
  website: string | null;
};

async function enrichExistingCompanyIds(
  conflicts: ImportConflictRow[],
  workspaceId: string,
): Promise<ImportConflictRow[]> {
  if (conflicts.length === 0) return conflicts;

  const nameKeys = [
    ...new Set(
      conflicts
        .map((c) => (c.companyName || c.name || '').trim())
        .filter((n) => n.length > 0),
    ),
  ].slice(0, 80);

  const domainKeys = [
    ...new Set(
      conflicts
        .map((c) => (c.domain || '').trim().toLowerCase())
        .filter((d) => d.length > 0),
    ),
  ].slice(0, 80);

  const nameToMatch = new Map<string, CompanyMatch>();
  const domainToMatch = new Map<string, CompanyMatch>();

  await Promise.all([
    ...nameKeys.map(async (name) => {
      const { data } = await supabase
        .from('companies')
        .select('id, name, domain, website')
        .eq('workspace_id', workspaceId)
        .is('deleted_at', null)
        .ilike('name', name)
        .limit(1)
        .maybeSingle();
      if (data?.id) {
        nameToMatch.set(name.toLowerCase(), {
          id: data.id,
          name: data.name,
          domain: data.domain,
          website: data.website,
        });
      }
    }),
    ...domainKeys.map(async (domain) => {
      const { data } = await supabase
        .from('companies')
        .select('id, name, domain, website')
        .eq('workspace_id', workspaceId)
        .is('deleted_at', null)
        .eq('domain', domain)
        .limit(1)
        .maybeSingle();
      if (data?.id) {
        domainToMatch.set(domain, {
          id: data.id,
          name: data.name,
          domain: data.domain,
          website: data.website,
        });
      }
    }),
  ]);

  return conflicts.map((c) => {
    if (c.existingCompanyId) return c;
    const companyKey = (c.companyName || c.name || '').trim().toLowerCase();
    const domainKey = (c.domain || '').trim().toLowerCase();
    // Prefer name match for "same name, different website" identity conflicts.
    const match =
      (companyKey ? nameToMatch.get(companyKey) : undefined) ??
      (domainKey ? domainToMatch.get(domainKey) : undefined) ??
      null;
    if (!match) return c;
    return {
      ...c,
      existingCompanyId: match.id,
      existingCompanyName: match.name,
      existingCompanyWebsite: match.website || match.domain,
    };
  });
}

function openCompany(companyId: string) {
  // Pill can render outside BrowserRouter — prefer SPA path if router is live.
  const path = `/companies/${companyId}`;
  try {
    if (typeof window !== 'undefined' && window.history) {
      window.location.assign(path);
      return;
    }
  } catch {
    // fall through
  }
  window.location.href = path;
}

export function CSVImportConflictReviewDialog({
  jobId,
  open,
  onOpenChange,
  workspaceId: workspaceIdProp = null,
  onAllResolved,
}: CSVImportConflictReviewDialogProps) {
  const queryClient = useQueryClient();
  const contextWorkspaceId = useOptionalWorkspace()?.workspace?.id ?? null;
  const [resolvedRows, setResolvedRows] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    if (!open) setResolvedRows(new Set());
  }, [open, jobId]);

  const jobQuery = useQuery({
    enabled: open && !!jobId,
    queryKey: ['csv-import-job-conflict-review', jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('csv_import_jobs')
        .select(
          'id, workspace_id, entity_type, label, status, failed_rows, errors, csv_data, stats',
        )
        .eq('id', jobId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Import job not found');
      return data;
    },
    staleTime: 30_000,
  });

  const workspaceId =
    workspaceIdProp ||
    contextWorkspaceId ||
    (typeof jobQuery.data?.workspace_id === 'string'
      ? jobQuery.data.workspace_id
      : null);

  const [conflicts, setConflicts] = useState<ImportConflictRow[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!jobQuery.data || !workspaceId) {
        setConflicts([]);
        setRows([]);
        setOtherErrors([]);
        return;
      }
      const built = buildConflictsFromImportJob(jobQuery.data);
      setRows(built.rows);
      setOtherErrors(built.otherErrors);
      setEnriching(true);
      try {
        const enriched = await enrichExistingCompanyIds(
          built.conflicts,
          workspaceId,
        );
        if (!cancelled) setConflicts(enriched);
      } catch (e) {
        log.warn('Could not enrich existing company ids for conflict review', {
          error: e instanceof Error ? e.message : String(e),
        });
        if (!cancelled) setConflicts(built.conflicts);
      } finally {
        if (!cancelled) setEnriching(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [jobQuery.data, workspaceId]);

  const remaining = conflicts.filter((c) => !resolvedRows.has(c.row));
  const label = jobQuery.data?.label || 'Company import';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>A few companies need a choice</DialogTitle>
          <DialogDescription>
            {label}
            {conflicts.length > 0
              ? ` — ${remaining.length} of ${conflicts.length} still need a decision`
              : ''}
            . Nothing is broken: we paused so you do not get duplicate companies.
          </DialogDescription>
        </DialogHeader>

        {jobQuery.isLoading || enriching ? (
          <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading companies that need a choice…</span>
          </div>
        ) : jobQuery.isError ? (
          <Alert variant="destructive">
            <AlertDescription>
              {jobQuery.error instanceof Error
                ? jobQuery.error.message
                : 'Could not load this import'}
            </AlertDescription>
          </Alert>
        ) : conflicts.length === 0 ? (
          <div className="space-y-3 py-4">
            <p className="text-sm text-muted-foreground">
              No company name conflicts left on this import
              {otherErrors.length > 0
                ? ` (${otherErrors.length} other issue${otherErrors.length === 1 ? '' : 's'} are not reviewable here).`
                : '.'}
            </p>
            {otherErrors.length > 0 ? (
              <ul className="text-xs text-muted-foreground list-disc list-inside max-h-40 overflow-y-auto">
                {otherErrors.slice(0, 15).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            ) : null}
            <Button type="button" onClick={() => onOpenChange(false)} className="w-full">
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <CSVCompanyConflictReview
              jobId={jobId}
              workspaceId={workspaceId}
              conflicts={conflicts}
              parsedData={rows}
              onOpenCompany={(companyId) => {
                onOpenChange(false);
                openCompany(companyId);
              }}
              onResolved={(row) => {
                setResolvedRows((prev) => {
                  const next = new Set(prev).add(row);
                  const left = conflicts.filter((c) => !next.has(c.row));
                  if (left.length === 0) {
                    queryClient.invalidateQueries({ queryKey: ['csv-import-jobs'] });
                    onAllResolved?.();
                  }
                  return next;
                });
              }}
            />
            {remaining.length === 0 ? (
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ['csv-import-jobs'] });
                  onAllResolved?.();
                  onOpenChange(false);
                }}
              >
                Done
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => onOpenChange(false)}
              >
                Close for now — finish later
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
